// src/Components/Posts/AllPosts.jsx
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import "./allposts.css";
import BarLoading from "../Loading/BarLoading";
import PostCards from "../PostCards/PostCards";
import { AllPostContext } from "../../contextStore/AllPostContext";
import client from "../../api/client";

const PAGE_SIZE = 20;

// client-priority popular order
const PRIORITY_ORDER = [
  "Textile Industries",
  "Incense and Dhoop Industries",
  "Others",
];

function AllPosts() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { loading: ctxLoading } = useContext(AllPostContext);

  const [searchParams] = useSearchParams();
  const initialSort = searchParams.get("sort") || "newest";
  const page = Number(searchParams.get("page") || 1);

  const [sortOrder, setSortOrder] = useState(initialSort);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [popularCategories, setPopularCategories] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  /* ---------------- FETCH POSTS ---------------- */
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    async function fetchPosts() {
      try {
        const params = new URLSearchParams({
          sort: sortOrder === "oldest" ? "old" : "new",
          limit: PAGE_SIZE,
          page,
        });

        if (slug && slug !== "all") {
          params.append("category", slug.replace(/-/g, " "));
        }

        const res = await client.get(`/api/machines?${params.toString()}`);
        if (!mounted) return;

        let fetched = Array.isArray(res.data?.machines)
          ? res.data.machines
          : [];

        if (sortOrder === "priceLowHigh")
          fetched.sort((a, b) => Number(a.price) - Number(b.price));
        if (sortOrder === "priceHighLow")
          fetched.sort((a, b) => Number(b.price) - Number(a.price));

        setPosts(page === 1 ? fetched : (prev) => [...prev, ...fetched]);
      } catch (err) {
        console.error("AllPosts fetch error:", err);
        if (mounted) setPosts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchPosts();
    return () => (mounted = false);
  }, [slug, sortOrder, page]);

  /* ---------------- FETCH CATEGORIES ---------------- */
  useEffect(() => {
    let mounted = true;

    async function fetchCategories() {
      try {
        const res = await client.get("/api/categories");
        if (!mounted) return;

        const cats = Array.isArray(res.data?.categories)
          ? res.data.categories
          : [];

        setCategories(cats);

        // priority categories first
        const priority = PRIORITY_ORDER
          .map((name) => cats.find((c) => c.name === name))
          .filter(Boolean);

        const remaining = cats
          .filter((c) => !PRIORITY_ORDER.includes(c.name))
          .sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0));

        setPopularCategories([...priority, ...remaining].slice(0, 6));
      } catch (err) {
        console.warn("Category API failed, using fallback");
        const fallback = [
          { id: 1, name: "Textile Industries", slug: "textile-industries" },
          { id: 2, name: "Incense and Dhoop Industries", slug: "incense-and-dhoop-industries" },
          { id: 3, name: "Others", slug: "others" },
        ];
        setCategories(fallback);
        setPopularCategories(fallback);
      }
    }

    fetchCategories();
    return () => (mounted = false);
  }, []);

  /* ---------------- HANDLERS ---------------- */
  function handleSortChange(e) {
    const newSort = e.target.value;
    setSortOrder(newSort);
    navigate(`/category/${slug || "all"}?sort=${newSort}&page=1`);
  }

  function handleLoadMore() {
    navigate(`/category/${slug || "all"}?sort=${sortOrder}&page=${page + 1}`);
  }

  function handleCategoryClick(cat) {
    const catSlug =
      cat.slug || cat.name.replace(/\s+/g, "-").toLowerCase();

    setIsDropdownOpen(false);
    navigate(`/category/${catSlug}?sort=${sortOrder}&page=1`);
  }

  function isActiveCategory(cat) {
    const catSlug = cat.slug || cat.name.replace(/\s+/g, "-").toLowerCase();
    return (slug || "all") === catSlug;
  }

  /* ---------------- ICON MAP ---------------- */
  function getCategoryIcon(name) {
    const icons = {
      "Textile Industries": "🧵",
      "Incense and Dhoop Industries": "🪔",
      "Food Industries": "🍕",
      "Pharma Industries": "💊",
      "Construction": "🏗️",
      "Cosmetic": "💄",
      "Primary Packaging": "📦",
      "Secondary Packaging": "🎁",
      "Stationary": "📐",
      "Parts Counting Machinery": "🔢",
      "Parts Assembly Machine": "⚙️",
      "Special Purpose Machines": "🛠️",
      "Gems and Jewellery": "💎",
      "Non Woven Product Machinery": "🧻",
      "Others": "📦",
    };
    return icons[name] || "📦";
  }

  const categoryName = slug ? slug.replace(/-/g, " ") : "Industrial Machinery";

  /* ---------------- JSX ---------------- */
  return (
    <div className="all-posts-container">
      <div className="all-posts-header">
        <h1 className="page-title">{categoryName}</h1>
        <p className="page-subtitle">
          Premium industrial equipment for your business needs
        </p>
      </div>

      <section className="all-posts-content">
        <div className="content-header">
          <h2 className="section-title">Available Machinery</h2>

          <select
            className="sort-select"
            value={sortOrder}
            onChange={handleSortChange}
          >
            <option value="newest">Newest First</option>
            <option value="priceLowHigh">Price: Low to High</option>
            <option value="priceHighLow">Price: High to Low</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        {/* Popular Categories */}
        <div className="category-filter-section">
          <div className="popularCategories">
            {popularCategories.map((cat) => (
              <div
                key={cat.id || cat.name}
                className={`categoryCard ${isActiveCategory(cat) ? "active" : ""
                  }`}
                onClick={() => handleCategoryClick(cat)}
              >
                <div className="categoryIcon">{getCategoryIcon(cat.name)}</div>
                <span className="categoryName">{cat.name}</span>
              </div>
            ))}
          </div>

          <div className="viewAllCategories">
            <button
              className="viewAllButton"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              View All Categories
            </button>

            {isDropdownOpen && (
              <div className="allCategoriesDropdown">
                <div className="dropdownGrid">
                  {categories.map((cat) => (
                    <div
                      key={cat.id || cat.name}
                      className="dropdownCategory"
                      onClick={() => handleCategoryClick(cat)}
                    >
                      <span className="dropdownIcon">
                        {getCategoryIcon(cat.name)}
                      </span>
                      {cat.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Products */}
        <div className="products-grid-section">
          {loading && page === 1 ? (
            <BarLoading />
          ) : (
            <div className="products-grid">
              {posts.map((p) => (
                <div className="product-card" key={p._id}>
                  <PostCards product={p} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="load-more-section">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={ctxLoading || loading}
          >
            Load More Machinery
          </button>
        </div>
      </section>
    </div>
  );
}

export default AllPosts;
